import {
  RichiestaPreventivoDto,
  RichiestaPreventivoTipo,
  Role,
  TipoOlioLavorazioni,
  useGetRichiestePreventivo,
  useGetUsers,
} from "@/api";
import Box from "@/elements/Box";
import Table from "@/elements/Table";
import Typography from "@/elements/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RichiestaPreventivoDetailDialog } from "../RichiestaPreventivoDetailDialog";
import Loading from "../Loading";
import LoadingError from "../LoadingError";

const columnHelper = createColumnHelper<RichiestaPreventivoDto>();

function RichiestePreventivoPage() {
  const { t } = useTranslation();

  const tipoLabel = useCallback(
    (tipo: RichiestaPreventivoDto["tipo"]): string => {
      switch (tipo) {
        case RichiestaPreventivoTipo.lubrifica:
          return t("richiestePreventivoPage.tipi.lubrifica");
        case RichiestaPreventivoTipo.lavorazioniMeccaniche:
          return t("richiestePreventivoPage.tipi.lavorazioniMeccaniche");
        default:
          return "-";
      }
    },
    [t]
  );

  const tipoOlioLavorazioniLabel = useCallback(
    (tipo: RichiestaPreventivoDto["tipoOlioLavorazioni"]): string => {
      switch (tipo) {
        case TipoOlioLavorazioni.olioIntero:
          return t("richiestePreventivoPage.tipiOlio.olioIntero");
        case TipoOlioLavorazioni.lubrorefrigerante:
          return t("richiestePreventivoPage.tipiOlio.lubrorefrigerante");
        default:
          return "-";
      }
    },
    [t]
  );

  const [filtroEmail, setFiltroEmail] = useState("");
  const [filtroUserCommercialeId, setFiltroUserCommercialeId] = useState<
    number | ""
  >("");
  const [selectedRichiesta, setSelectedRichiesta] =
    useState<RichiestaPreventivoDto | null>(null);

  const { isLoading, error, data } = useGetRichiestePreventivo(
    filtroUserCommercialeId !== "" ? { userCommercialeId: filtroUserCommercialeId } : undefined
  );
  const { data: usersData } = useGetUsers({ role: Role.Commerciale });
  const navigate = useNavigate();

  const users = usersData?.data ?? [];

  const columns = useMemo(
    () => [
      columnHelper.accessor("nomeAzienda", {
        header: t("richiestePreventivoPage.colonne.azienda"),
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      columnHelper.accessor("nomeRichiedente", {
        header: t("richiestePreventivoPage.colonne.richiedente"),
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      columnHelper.accessor("email", {
        header: t("richiestePreventivoPage.colonne.email"),
        cell: ({ getValue }) => getValue() ?? "-",
      }),
      columnHelper.accessor(
        (row) => tipoLabel(row.tipo),
        {
          id: "tipo",
          header: t("richiestePreventivoPage.colonne.tipoRichiesta"),
          cell: ({ row }) => {
            const tipo = row.original.tipo;
            if (!tipo) return "-";
            const isLubrifica = tipo === RichiestaPreventivoTipo.lubrifica;
            return (
              <Chip
                label={tipoLabel(tipo)}
                size="small"
                sx={{
                  backgroundColor: isLubrifica ? "#e3f2fd" : "#f3e5f5",
                  color: isLubrifica ? "#1565c0" : "#6a1b9a",
                  fontWeight: 500,
                }}
              />
            );
          },
        }
      ),
      columnHelper.accessor(
        (row) =>
          row.tipo === RichiestaPreventivoTipo.lavorazioniMeccaniche
            ? tipoOlioLavorazioniLabel(row.tipoOlioLavorazioni)
            : "",
        {
          id: "tipoOlioLavorazioni",
          header: t("richiestePreventivoPage.colonne.tipoOlio"),
          cell: ({ row }) => {
            if (
              row.original.tipo !==
              RichiestaPreventivoTipo.lavorazioniMeccaniche
            ) {
              return "-";
            }
            return tipoOlioLavorazioniLabel(row.original.tipoOlioLavorazioni);
          },
        }
      ),
      columnHelper.accessor("vuoleEssereRicontattato", {
        header: t("richiestePreventivoPage.colonne.ricontatto"),
        cell: ({ getValue }) => {
          const val = getValue();
          if (val === true) {
            return (
              <CheckCircleOutlineIcon
                fontSize="small"
                sx={{ color: "success.main", display: "block" }}
              />
            );
          }
          if (val === false) {
            return (
              <HighlightOffIcon
                fontSize="small"
                sx={{ color: "text.disabled", display: "block" }}
              />
            );
          }
          return "-";
        },
      }),
      columnHelper.accessor(
        (row) =>
          row.userCommerciale
            ? `${row.userCommerciale.name ?? ""} ${row.userCommerciale.lastName ?? ""}`.trim()
            : "",
        {
          id: "userCommerciale",
          header: t("richiestePreventivoPage.colonne.commerciale"),
          cell: ({ row }) => {
            const u = row.original.userCommerciale;
            if (!u) return "-";
            return `${u.name ?? ""} ${u.lastName ?? ""}`.trim() || u.email || "-";
          },
        }
      ),
      columnHelper.accessor("createdAt", {
        header: t("richiestePreventivoPage.colonne.data"),
        cell: ({ getValue }) => {
          const val = getValue();
          if (!val) return "-";
          return new Date(val).toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 60,
        cell: ({ row }) => (
          <IconButton
            size="small"
            color="primary"
            title={t("richiestePreventivoPage.azioni.dettaglio")}
            onClick={() => setSelectedRichiesta(row.original)}
          >
            <InfoOutlinedIcon />
          </IconButton>
        ),
      }),
    ],
    [t, tipoLabel, tipoOlioLavorazioniLabel]
  );

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return <LoadingError error={error} />;
  }

  const richieste = data.data ?? [];

  const richiesteFiltrate = filtroEmail
    ? richieste.filter((r) =>
        r.email?.toLowerCase().includes(filtroEmail.toLowerCase())
      )
    : richieste;

  return (
    <Box p={2}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("richiestePreventivoPage.titolo")}
        </Typography>
        <Box display="flex" gap={1} pt={1} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t("richiestePreventivoPage.filtri.commerciale")}</InputLabel>
            <Select
              label={t("richiestePreventivoPage.filtri.commerciale")}
              value={filtroUserCommercialeId}
              onChange={(e) =>
                setFiltroUserCommercialeId(e.target.value as number | "")
              }
            >
              <MenuItem value="">{t("richiestePreventivoPage.filtri.tutti")}</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {`${u.name ?? ""} ${u.lastName ?? ""}`.trim() || u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder={t("richiestePreventivoPage.filtri.cercaEmail")}
            value={filtroEmail}
            onChange={(e) => setFiltroEmail(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ ml: 1.5 }} /> }}
          />
        </Box>
      </Box>

      <Table
        columns={columns}
        data={richiesteFiltrate}
        sortable={true}
        initialSort={[{ id: "createdAt", desc: true }]}
        noRowsText={t("richiestePreventivoPage.stati.nessuna")}
        onRowClick={(row) => navigate(`/richieste_risparmi/${row.original.id}`)}
      />

      <RichiestaPreventivoDetailDialog
        open={selectedRichiesta !== null}
        richiesta={selectedRichiesta}
        onClose={() => setSelectedRichiesta(null)}
      />
    </Box>
  );
}

export default RichiestePreventivoPage;
